import { useEffect, useRef } from 'react'
import { AssetManager } from '@/components/original/games/slide/core/AssetManager'
import { SoundManager } from '@/components/original/games/slide/core/SoundManager'
import type { TileDefinition } from '@/types/slide'

interface TileWinEffectProps {
	tile: TileDefinition
}

export function TileWinEffect({ tile }: TileWinEffectProps) {
	const containerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const container = containerRef.current
		if (!container) {
			return undefined
		}

		SoundManager.instance.playTileSound(tile.tier)

		const sprite = AssetManager.getTierImage(tile.tier)
		if (!sprite || !sprite.complete || sprite.naturalWidth === 0) {
			return undefined
		}

		const width = Math.max(container.offsetWidth, container.clientWidth, 64)
		const height = Math.max(container.offsetHeight, container.clientHeight, 64)

		const canvas = document.createElement('canvas')
		canvas.width = width
		canvas.height = height
		canvas.style.position = 'absolute'
		canvas.style.inset = '0'
		canvas.style.width = '100%'
		canvas.style.height = '100%'
		canvas.style.pointerEvents = 'none'
		container.appendChild(canvas)

		const context = canvas.getContext('2d')
		if (!context) {
			container.removeChild(canvas)
			return undefined
		}

		const frameWidth = sprite.naturalWidth / tile.spriteColumns
		const frameHeight = sprite.naturalHeight / tile.spriteRows
		const drawSize = Math.min(canvas.width, canvas.height) * 1.05

		let column = 0
		let row = 0
		let finished = false

		const draw = () => {
			context.clearRect(0, 0, canvas.width, canvas.height)
			context.save()
			context.translate(canvas.width / 2, canvas.height / 2)
			context.drawImage(sprite, frameWidth * column, frameHeight * row, frameWidth, frameHeight, -drawSize / 2, -drawSize / 2, drawSize, drawSize)
			context.restore()

			if (finished) {
				return
			}

			column += 1
			if (column >= tile.spriteColumns) {
				column = 0
				row += 1
				if (row >= tile.spriteRows) {
					row = tile.spriteRows - 1
					column = tile.spriteColumns - 1
					finished = true
				}
			}
		}

		draw()
		const interval = window.setInterval(draw, 60)

		return () => {
			window.clearInterval(interval)
			if (container.contains(canvas)) {
				container.removeChild(canvas)
			}
		}
	}, [tile])

	return <div ref={containerRef} className='pointer-events-none absolute inset-0 z-[2] h-full w-full' />
}
